import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import type { SvgIconComponent } from '@mui/icons-material';

import { Roles, type Role } from '@/constants/roles';
import { Routes } from '@/constants/routes';

// Top-nav navigation modeled as data (ADR-0035 / DESIGN.md §5). Two groups:
// PROVOZ → Objednávky; SPRÁVA → Uživatelé + Notifikace (admin notification
// audit, CHR-01 / ADR-0034). Registries are autocomplete sub-resources, audit
// is a modal — none are navigable, so none appear. Expand this table when more
// top-level routes ship.
//
// Role visibility uses the SAME logic as the old Topbar nav:
// `allowedRoles.some(r => userRoles.includes(r))`. Items the role can't see
// are HIDDEN (not disabled); a group with no visible item is hidden entirely
// (DESIGN.md §16).
export interface NavItem {
  readonly to: string;
  readonly labelKey: string;
  // `package` / `users` per DESIGN.md icon library — mapped to MUI icons.
  readonly icon: SvgIconComponent;
  readonly allowedRoles: readonly Role[];
}

export interface NavGroup {
  readonly labelKey: string;
  readonly items: readonly NavItem[];
}

export const NAV_GROUPS: readonly NavGroup[] = [
  {
    labelKey: 'nav.group.operations',
    items: [
      {
        to: Routes.ORDERS,
        labelKey: 'nav.orders',
        icon: Inventory2OutlinedIcon,
        // CHR-03 §2 / ADR-0046 — `viewer` reaches the read-only overview.
        allowedRoles: [Roles.BACKOFFICE, Roles.DISPATCHER, Roles.VIEWER],
      },
    ],
  },
  {
    labelKey: 'nav.group.administration',
    items: [
      {
        to: Routes.ADMIN_USERS,
        labelKey: 'nav.users',
        icon: PeopleOutlinedIcon,
        allowedRoles: [Roles.ADMIN],
      },
      {
        to: Routes.ADMIN_NOTIFICATIONS,
        labelKey: 'nav.notifications',
        icon: NotificationsOutlinedIcon,
        allowedRoles: [Roles.ADMIN],
      },
    ],
  },
];

// Filter a nav group to the items the given roles may see. Returns null if no
// item is visible (so the caller can drop the whole group — hide-not-disable).
export const visibleGroupItems = (
  group: NavGroup,
  userRoles: readonly Role[],
): readonly NavItem[] =>
  group.items.filter((item) =>
    item.allowedRoles.some((r) => userRoles.includes(r)),
  );
